/**
 * Play/pause button component for controlling a single audio track via custom DOM events.
 *
 * @module components/common/AudioPlayButton
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useEffect, useState, useCallback} from "react";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import PriorityHighRoundedIcon from "@material-ui/icons/PriorityHighRounded";
import LoadingIcon from "@material-ui/icons/Loop";
import {Tooltip} from "@material-ui/core";
import PropTypes from "prop-types";

/**
 * AudioPlayButton is a React functional component responsible for rendering and managing
 * the user interaction with the audio playback button. It handles states such as whether
 * the audio is playing, paused, or unavailable, and integrates with custom audio events
 * to control playback behavior.
 *
 * Properties:
 *
 * @param {string | number} audio_id - A unique identifier for the audio file associated with this button.
 * @param {Object} classes - An object containing CSS class names for styling various components.
 *
 * State Management:
 * - `isPlaying`: Determines whether the audio associated with the button is currently playing.
 * - `status`: Represents the current playback status, such as 'waiting', 'loaded', 'playing', 'paused', or 'failed'.
 * - `audioUnavailable`: Indicates whether the audio resource is unavailable based on the `unavailable` prop.
 *
 * Events:
 * - `audio-end`: Dispatched when the audio playback ends. Triggers the `onAudioEnd` method.
 * - `audio-stop`: Dispatched when the audio playback is manually stopped. Triggers the `onAudioEnd` method.
 * - `audio-play`: Dispatched when the audio playback starts. Triggers the `onAudioPlay` method.
 * - `audio-pause`: Dispatched when the audio playback is paused. Triggers the `onAudioPause` method.
 * - `audio-button-click`: A custom event dispatched when the play button is clicked. Contains the `audio_id` and `status` in its payload.
 *
 * Rendering:
 * - If the audio is unavailable or loading has failed, a tooltip with a priority icon is displayed.
 * - While the audio status is in `waiting` state, a loading spinner is shown.
 * - Based on the playback state, the button will dynamically render a play, pause, or play-pause icon,
 *   each of which can trigger different playback actions via the `onClick` handler.
 */
const AudioPlayButton = ({
    audio_id,
    classes
}) => {
    const [audioId, setAudioId] = useState(audio_id);
    const [isPlaying, setIsPlaying] = useState(false);
    const [status, setStatus] = useState('waiting');
    const [audioUnavailable, setAudioUnavailable] = useState(false);

    useEffect(
        () => {
            setAudioId(audio_id);
        },
        [audio_id]
    );

    useEffect(
        () => {
            setIsPlaying(status === 'playing');
        },
        [status]
    );

    useEffect(
        () => {
            initEventListeners();

            return () => {
                clearEventListeners();
            }
        }
    );

    /**
     * Initializes event listeners for audio-related events.
     *
     * This function sets up listeners for the following custom events:
     * - `audio-end`: Triggered when audio playback ends. Executes the `onAudioEnd` method.
     * - `audio-stop`: Triggered when audio playback is stopped. Executes the `onAudioEnd` method.
     * - `audio-play`: Triggered when audio playback starts. Executes the `onAudioPlay` method.
     * - `audio-pause`: Triggered when audio playback is paused. Executes the `onAudioPause` method.
     *
     * Each event is bound to its corresponding handler with the current context.
     */
    const initEventListeners = () => {
        document.addEventListener('audio-end', onAudioEnd);
        document.addEventListener('audio-stop', onAudioEnd);
        document.addEventListener('audio-play', onAudioPlay);
        document.addEventListener('audio-pause', onAudioPause);
        document.addEventListener('audio-ready', onAudioReady);
        document.addEventListener('audio-unavailable', onAudioUnavailable);
        document.addEventListener('audio-button-click', onAudioButtonClick);
    }

    /**
     * Removes specific event listeners related to audio actions from the document object.
     *
     * The removed event listeners include:
     * - 'audio-end': Triggered when audio playback ends.
     * - 'audio-stop': Triggered when audio playback is manually stopped.
     * - 'audio-play': Triggered when audio playback starts.
     * - 'audio-pause': Triggered when audio playback is paused.
     *
     * This function ensures that previously bound event handlers for audio actions
     * (onAudioEnd, onAudioPlay, and onAudioPause) are unbound to prevent memory leaks
     * or unintended behavior.
     */
    const clearEventListeners = () => {
        document.removeEventListener('audio-end', onAudioEnd);
        document.removeEventListener('audio-stop', onAudioEnd);
        document.removeEventListener('audio-play', onAudioPlay);
        document.removeEventListener('audio-pause', onAudioPause);
        document.removeEventListener('audio-ready', onAudioReady);
        document.removeEventListener('audio-unavailable', onAudioUnavailable);
        document.removeEventListener('audio-button-click', onAudioButtonClick);
    }

    /**
     * Callback function triggered when the audio is ready.
     *
     * This function verifies if the event's audio ID matches the provided audioId,
     * and updates the audio availability status accordingly.
     *
     * Dependencies:
     * - `audioId`: The expected audio identifier to be matched against the event details.
     * - `status`: A dependency determining when the callback should be recreated.
     *
     * Event Details:
     * - `ev.detail.id`: Represents the ID of the audio in the event object.
     *
     * Side Effects:
     * - Updates the `setAudioUnavailable` state to `false` when the condition is met.
     */
    const onAudioReady = useCallback((ev) => {
        if (ev.detail.id === audioId) {
            setAudioUnavailable(false);
            if (!isPlaying && (['waiting', 'failed'].includes(status))) {
                setStatus('loaded');
            }
        }
    }, [audioId, status, isPlaying]);

    /**
     * Callback function that handles the scenario when an audio resource becomes unavailable.
     *
     * The function is triggered when a custom event is emitted and checks if the audio ID
     * in the event details matches the specified `audioId`. If they match, it updates
     * the state to indicate that the audio resource is unavailable.
     *
     * Dependencies:
     * - `audioId`: The identifier of the audio resource being monitored.
     * - `status`: A dependent variable upon which the callback re-evaluates.
     *
     * @param {CustomEvent} ev - The event object containing details about the audio resource.
     * @param {Object} ev.detail - Contains additional data relating to the event.
     * @param {string} ev.detail.id - The ID of the audio resource included in the event.
     */
    const onAudioUnavailable = useCallback((ev) => {
        if (ev.detail.id === audioId) {
            setAudioUnavailable(true);
            setStatus('failed');
            console.log('Audio is unavailable for button %s!', audioId);
        }
    }, [audioId]);

    /**
     * Event handler for the "audio-play" event.
     *
     * This function processes an event triggered when an audio playback is started.
     * It compares the index from the event details with the `audioId` and updates
     * the playback status and playing state accordingly. If the index matches, it
     * sets the status to "playing" and updates the playing state to true. Otherwise,
     * it sets the status to "loaded" and the playing state to false.
     *
     * @param {Object} evt - The event object associated with the "audio-play" event.
     * @param {Object} evt.detail - The detail object inside the event, containing the data associated with the audio event.
     * @param {number} evt.detail.index - The index of the audio currently being played, as provided in the event details.
     */
    const onAudioPlay = useCallback((evt) => {
        if (evt.detail.index === audioId) {
            setStatus('playing');
        }
        setIsPlaying(evt.detail.index === audioId);
    }, [audioId]);  // Re-create if audioId changes

    /**
     * Event handler for the `audio-pause` event.
     *
     * This function executes when the `audio-pause` event is triggered. It checks
     * if the event's audio index matches the current audio ID. If they match, it
     * updates the application state to reflect that the audio has been paused.
     *
     * @param {Object} evt - The event object associated with the `audio-pause` event.
     * @param {Object} evt.detail - A detail object containing event-specific data.
     * @param {number} evt.detail.index - The index of the audio source associated with the event.
     */
    const onAudioPause = useCallback((evt) => {
        if (evt.detail.index === audioId) {
            setStatus('paused');
            setIsPlaying(false);
        }
    }, [audioId]);

    /**
     * Event handler triggered when the audio playback ends.
     *
     * This function listens for the `audio-end` event and performs
     * the following actions:
     * - Logs the event details for debugging purposes.
     * - Checks if the ending audio matches the provided `audioId`.
     * - Updates the playback state to indicate the audio is no longer playing.
     * - Changes the audio status to 'loaded' to reflect its readiness.
     * - Logs relevant information about the audio and its state after playback has ended.
     *
     * @param {Object} evt - The event object associated with the `audio-end` event.
     * @param {Object} evt.detail - Detailed information about the event.
     * @param {number} evt.detail.index - The index of the audio that triggered the event.
     */
    const onAudioEnd = useCallback((evt) => {
        if (evt.detail.index === audioId) {
            setIsPlaying(false);
            setStatus('loaded');
        }
    }, [audioId]);

    /**
     * Callback function to handle the click event on an audio-related button.
     * It checks if the audio ID exists and whether the clicked audio's ID
     * matches the given audio ID. If they do not match, it updates the status
     * to 'loaded'.
     *
     * Dependencies:
     * - `audioId`: The current audio ID to compare against the event's audio ID.
     * - `setStatus`: Function used to update the operational status of the audio.
     *
     * Parameters:
     * - evt: The event object triggered by the button click. It is expected to
     *   contain a property `detail.audio_id` representing the audio ID associated
     *   with the event.
     *
     * Dependencies Array:
     * - `audioId`: Ensures this callback's reference is updated when `audioId` changes.
     */
    const onAudioButtonClick = useCallback((evt) => {
        if ( audioId && (evt.detail.audio_id !== audioId) ) {
            setStatus('loaded');
        }
    }, [audioId]);

    /**
     * Event handler function triggered when an associated button or element is clicked.
     * Dispatches a custom event named 'audio-button-click' with additional details.
     *
     * @fires CustomEvent:audio-button-click
     * @property {Object} detail - Custom data associated with the dispatched event.
     * @property {string} detail.audio_id - The identifier of the related audio.
     * @property {string} detail.status - The current status of the audio.
     */
    const onClick = () => {
        if (audioId) {
            const ev = new CustomEvent(
                'audio-button-click',
                {
                    detail: {
                        audio_id: audioId,
                        status: status
                    },
                }
            );
            document.dispatchEvent(ev);
        } else {
            throw new Error('Click event initiated while Audio ID is not defined!');
        }
    }

    /**
     * Renders the appropriate icon or tooltip based on the current audio state.
     *
     * This function evaluates various conditions related to the audio's availability,
     * playback status, and loading state, and returns the corresponding JSX element.
     * It provides visual feedback to the user with relevant icons or tooltips.
     *
     * Conditions:
     * - If audio is unavailable, shows a tooltip indicating "Audio is NOT available" with a priority icon.
     * - If the audio status is not 'loaded', 'playing', or 'paused', displays either a loading icon or a tooltip
     *   if the status is 'failed'.
     * - If the audio is not currently playing, displays a play icon which triggers an action on click.
     * - If the audio is paused, displays a play-pause icon which triggers an action on click.
     * - Defaults to showing a pause icon with a click action when audio is playing.
     *
     * Dependencies:
     * - Classes (e.g., `classes.priorityIcon`, `classes.audioIcon`, `classes.loading`) for styling the icons.
     * - Tooltip and various Icon components for UI presentation and interactivity.
     *
     * External variables/functions:
     * - `audioUnavailable`: Boolean indicating whether audio is available.
     * - `status`: Current audio playback status (e.g., 'loaded', 'playing', 'paused', 'failed').
     * - `isPlaying`: Boolean indicating whether the audio is currently playing.
     * - `onClick`: Callback function triggered when interactive icons are clicked.
     *
     * @returns {JSX.Element | null} JSX element representing the rendered UI for the current audio state.
     */
    const render = () => {
        if (audioUnavailable || !audioId) {
            return (
                <Tooltip title="Audio is NOT available">
                    <PriorityHighRoundedIcon className={classes.priorityIcon} />
                </Tooltip>
            );
        }

        if ((status !== 'loaded') && (status !== 'playing') && (status !== 'paused')) {
            if (status === 'failed') {
                return (
                    <Tooltip title="Audio is NOT available">
                        <PriorityHighRoundedIcon className={classes.priorityIcon} />
                    </Tooltip>
                );
            }

            return (
                <Tooltip title="Audio is being loaded... Please wait">
                    <LoadingIcon className={classes.loading} />
                </Tooltip>
            );
        }

        if (!isPlaying || (status === 'paused')) {
            return (
                <PlayArrowIcon
                    className={classes.audioIcon}
                    onClick={onClick}
                />
            );
        }

        return (
            <PauseIcon
                className={classes.audioIcon}
                onClick={onClick}
            />
        );
    }

    return (
        <div id={audioId}>
            {render()}
        </div>
    );
}

AudioPlayButton.propTypes = {
    audio_id: PropTypes.string,
    classes: PropTypes.object,
};

export default AudioPlayButton;
